import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';

import { Box, Paper, Table, TableContainer } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useModal } from 'src/hooks/useModal';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { getComparator, applySortFilter } from 'src/utils/filterData';

import TableHeaders from 'src/components/tableHeaders/TableHeaders';
import TablePaginations from 'src/components/table-pagination/TablePaginations';

import TagsTableBody from './tableBody';
import MenuPopover from '../popovers/MenuPopover';
import { TABLE_HEAD_TAGS } from './TagTableHeaders';
import AddUpdateTags from '../modals/AddUpdateTags';

function TagsTable({
  tags,
  selectedTags,
  setSelectedTags,
  isLoading,
  filterName,
  filterBy,
  handleFetchTags,
  departmentList,
  page,
  setPage,
}) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const { isSmallScreen } = useResponsiveScreen();
  const [currentPageTags, setCurrentPageTags] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [indeterminateCheckbox, setIndeterminateCheckbox] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const { showModal, hideModal } = useModal();

  const isSelected = (id) => selectedTags.indexOf(id) !== -1;

  const filteredTags = useMemo(() => {
    const filteredData = applySortFilter(
      tags,
      getComparator(order, orderBy),
      filterName,
      filterBy
    );

    return filteredData;
  }, [filterName, order, orderBy, filterBy, tags]);

  useEffect(() => {
    // Update current page forms shareable when page, rowsPerPage, or formsShareable changes
    const startIdx = page * rowsPerPage;
    const endIdx = startIdx + rowsPerPage;
    setCurrentPageTags(filteredTags.slice(startIdx, endIdx));
  }, [page, rowsPerPage, filteredTags]);

  useEffect(() => {
    const newSelectdsId = currentPageTags.map((n) => n.id);
    if (newSelectdsId.every((id) => selectedTags.includes(id))) {
      setIndeterminateCheckbox(true);
    } else setIndeterminateCheckbox(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageTags, page, selectedTags]);

  const handleCloseMenu = () => {
    setPopoverAnchorEl(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset page to 0 whenever rows per page changes
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = () => {
    const newSelecteds = currentPageTags.map((n) => n.id);
    if (newSelecteds.every((id) => selectedTags.includes(id))) {
      setSelectedTags(selectedTags.filter((id) => !newSelecteds.includes(id)));
      return;
    }
    setSelectedTags([...new Set([...selectedTags, ...newSelecteds])]);
  };

  const { isNotFound, searchIsNotFound } = useMemo(() => {
    const isNotFound = !filteredTags.length;
    const searchIsNotFound = !filteredTags.length && !!filterName;
    return { isNotFound, searchIsNotFound };
  }, [filteredTags, filterName]);

  const handleOpenMenu = (event, row) => {
    setPopoverAnchorEl(event.currentTarget);
    setSelectedItem(row);
  };

  const handleOpenUpdateModal = (row) => {
    showModal({
      title: t('pages.tagsPage.updateTag'),
      Component: AddUpdateTags,
      props: {
        hideModal,
        handleFetchTags,
        row,
        departmentList,
      },
    });
  };

  return (
    <>
      <Box
        sx={{
          overflowX: 'auto',
          maxWidth: '100%',
        }}
      >
        <TableContainer component={Paper}>
          <Table size={isSmallScreen ? 'small' : 'medium'}>
            <TableHeaders
              order={order}
              orderBy={orderBy}
              headLabel={
                role.authority === 'ROLE_ADMIN'
                  ? TABLE_HEAD_TAGS
                  : TABLE_HEAD_TAGS.filter(
                      ({ name }) => !['department', 'createdBy'].includes(name)
                    )
              }
              rowCount={filteredTags.length}
              numSelected={selectedTags.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
              showCheckBox
              indeterminate={indeterminateCheckbox}
            />

            <TagsTableBody
              isLoading={isLoading}
              isNotFound={isNotFound}
              searchIsNotFound={searchIsNotFound}
              filteredTags={filteredTags}
              page={page}
              rowsPerPage={rowsPerPage}
              isSelected={isSelected}
              setSelectedTags={setSelectedTags}
              handleOpenMenu={handleOpenMenu}
              handleOpenUpdateModal={handleOpenUpdateModal}
              filterName={filterName}
            />
          </Table>
        </TableContainer>
      </Box>

      {/* Table Pagination */}
      <TablePaginations
        filteredEntity={filteredTags}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />

      {/* menu popover */}
      <MenuPopover
        popoverAnchorEl={popoverAnchorEl}
        handleCloseMenu={handleCloseMenu}
        selectedItem={selectedItem}
        handleFetchTags={handleFetchTags}
        AddUpdateTags={AddUpdateTags}
        setSelectedTags={setSelectedTags}
        departmentList={departmentList}
      />
    </>
  );
}

TagsTable.propTypes = {
  tags: PropTypes.array,
  selectedTags: PropTypes.array,
  setSelectedTags: PropTypes.func,
  isLoading: PropTypes.bool,
  filterName: PropTypes.string,
  filterBy: PropTypes.string,
  handleFetchTags: PropTypes.func,
  departmentList: PropTypes.array,
  page: PropTypes.number,
  setPage: PropTypes.func,
};

export default TagsTable;
